// frontend/src/pages/InviteAccept.js – NextScenes Group Invite Acceptance
// Build: 2026-01-30-invite-accept-page-v1
//
// Goals:
// - Keep invite acceptance inside NextScenes UI (like VerifyEmail)
// - Handle: missing token, not logged in, wrong account, success flow
// - Redirect to API accept endpoint ONLY when appropriate (and ideally backend redirects back to frontend)

import React, { useEffect, useMemo, useState } from "react";
import "../App.css";
import { API_BASE, api } from "../api";
import { useUser } from "../UserContext";

function extractToken() {
  try {
    // 1) Standard: /invite?token=...
    const qs = new URLSearchParams(window.location.search || "");
    const t1 = String(qs.get("token") || "").trim();
    if (t1) return t1;

    // 2) Back-compat: /invite?inviteToken=...
    const t2 = String(qs.get("inviteToken") || "").trim();
    if (t2) return t2;

    // 3) Hash form: /invite#token=...
    const hash = String(window.location.hash || "");
    if (!hash) return "";

    const idx = hash.toLowerCase().indexOf("token=");
    if (idx >= 0) {
      const tail = hash.slice(idx);
      const qs2 = new URLSearchParams(tail.replace(/^#/, "").replace(/^\//, ""));
      const t3 = String(qs2.get("token") || "").trim();
      if (t3) return t3;

      const m = tail.match(/token=([^&]+)/i);
      if (m && m[1]) return decodeURIComponent(m[1]);
    }

    return "";
  } catch {
    return "";
  }
}

function maskEmail(email) {
  const e = String(email || "").trim();
  if (!e.includes("@")) return e;
  const [u, d] = e.split("@");
  if (!u || !d) return e;
  if (u.length <= 2) return `${u[0] || ""}***@${d}`;
  return `${u.slice(0, 2)}***@${d}`;
}

function safeGetQs(name) {
  try {
    const qs = new URLSearchParams(window.location.search || "");
    return String(qs.get(name) || "").trim();
  } catch {
    return "";
  }
}

export default function InviteAccept({ onNavigate, onNavigatePage }) {
  const nav =
    typeof onNavigate === "function"
      ? onNavigate
      : typeof onNavigatePage === "function"
        ? onNavigatePage
        : null;

  const { booting, isAuthenticated, user, logout } = useUser();

  const token = useMemo(() => extractToken(), []);
  const status = useMemo(() => safeGetQs("status").toLowerCase(), []);
  const kindQs = useMemo(() => safeGetQs("kind").toLowerCase(), []);
  const intentQs = useMemo(() => safeGetQs("intent").toLowerCase(), []);
  const inviteEmailQs = useMemo(() => safeGetQs("inviteEmail"), []);
  const meQs = useMemo(() => safeGetQs("me"), []);

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const meEmail = useMemo(() => String(user?.email || "").trim().toLowerCase(), [user]);
  const displayIdentity = useMemo(() => user?.name || user?.username || user?.email || "", [user]);

  // Try to fetch invite metadata (optional, best-effort)
  // If backend doesn't have this endpoint, it fails silently and UI still works.
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteKind, setInviteKind] = useState(
    kindQs === "project"
      ? "project"
      : ""
  );

  useEffect(() => {
    let alive = true;

    async function loadMeta() {
      if (!token) return;
      setInviteEmail("");

      try {
        const projectData =
          await api(
            `/api/project-invitations/${encodeURIComponent(
              token
            )}/meta`,
            {
              method: "GET",
            }
          );

        const em =
          String(
            projectData?.inviteEmail ||
            projectData?.email ||
            ""
          )
            .trim()
            .toLowerCase();

        if (!alive) return;

        setInviteKind(
          "project"
        );

        if (em) {
          setInviteEmail(em);
        }

        return;
      } catch (
        projectError
      ) {
        if (
          projectError?.status &&
          projectError.status !== 400 &&
          projectError.status !== 404
        ) {
          if (!alive) return;

          setErr(
            "Could not verify the project invitation. Please try again."
          );

          return;
        }
      }

      if (
        kindQs === "project"
      ) {
        if (!alive) return;

        /*
         * The project token may become non-pending immediately
         * after acceptance/decline, so metadata can legitimately
         * stop resolving. The explicit kind query remains the
         * authority and must not fall through to legacy Story.
         */
        setInviteKind(
          "project"
        );

        return;
      }

      /*
       * Legacy Story invitation fallback.
       * Existing Story invitation behaviour remains unchanged.
       */
      setInviteKind(
        "story"
      );

      try {
        const data =
          await api(
            `/api/stories/group/invite/${encodeURIComponent(
              token
            )}/meta`,
            {
              method: "GET",
            }
          );

        const em =
          String(
            data?.inviteEmail ||
            data?.email ||
            ""
          )
            .trim()
            .toLowerCase();

        if (!alive) return;

        if (em) {
          setInviteEmail(em);
        }
      } catch {
        // Existing Story meta endpoint remains optional.
      }
    }

    loadMeta();
    return () => {
      alive = false;
    };
  }, [token, kindQs]);

  function currentInviteKind() {
    return (
      kindQs === "project" ||
      inviteKind === "project"
    )
      ? "project"
      : "story";
  }

  function preserveInviteContinuation(
    nextKind
  ) {
    try {
      window.localStorage.setItem(
        "ns.inviteToken",
        token
      );

      if (
        nextKind === "project"
      ) {
        window.localStorage.setItem(
          "ns.inviteKind",
          "project"
        );
      } else {
        window.localStorage.removeItem(
          "ns.inviteKind"
        );
      }
    } catch {}
  }

  function goLoginWithToken() {
    if (!nav) return;

    const nextKind =
      currentInviteKind();

    preserveInviteContinuation(
      nextKind
    );

    const qs =
      token
        ? `?inviteToken=${encodeURIComponent(
            token
          )}${
            nextKind === "project"
              ? "&inviteKind=project"
              : ""
          }`
        : "";

    try {
      window.history.replaceState(
        {},
        "",
        `/login${qs}`
      );
    } catch {}

    nav("login");
  }

  function goRegisterWithToken() {
    if (!nav) return;

    const nextKind =
      currentInviteKind();

    preserveInviteContinuation(
      nextKind
    );

    const qs =
      token
        ? `?inviteToken=${encodeURIComponent(
            token
          )}${
            nextKind === "project"
              ? "&inviteKind=project"
              : ""
          }`
        : "";

    try {
      window.history.replaceState(
        {},
        "",
        `/register${qs}`
      );
    } catch {}

    nav("register");
  }

  async function handleProceedAccept() {
    if (!token) return;

    setBusy(true);
    setErr("");
    setMsg("");

    try {
      // IMPORTANT:
      // This is a browser navigation because the backend currently performs redirects / html responses.
      // For best UX, backend should redirect back to FRONTEND_PUBLIC_URL/invite?token=...&status=ok
      const projectInvite =
        kindQs === "project" ||
        inviteKind === "project";

      const url =
        projectInvite
          ? `${API_BASE}/api/project-invitations/${encodeURIComponent(
              token
            )}/accept`
          : `${API_BASE}/api/stories/group/invite/${encodeURIComponent(
              token
            )}/accept`;

      window.location.assign(url);
    } catch {
      setErr("Could not open the invitation acceptance link. Please try again.");
      setBusy(false);
    }
  }

  async function handleDeclineInvitation() {
    if (!token) return;
    setBusy(true);
    setErr("");
    setMsg("");
    try {
      const isProject = currentInviteKind() === "project";
      if (isProject) {
        await api.post(`/api/project-invitations/${encodeURIComponent(token)}/decline`, {});
      } else {
        await api.post(`/api/stories/group/invite/${encodeURIComponent(token)}/reject`, {});
      }
      window.location.assign(
        `/invite?token=${encodeURIComponent(token)}&kind=${isProject ? "project" : "story"}&status=declined`
      );
    } catch (error) {
      setErr(String(error?.message || "Could not decline this invitation. Please try again."));
      setBusy(false);
    }
  }

  // Auto-run if the backend redirected here with status hints
  useEffect(() => {
    // If backend already told us "wrong_email", show a stable message.
    if (status === "wrong_email") {
      const inv = inviteEmailQs || inviteEmail;
      const me = meQs || meEmail;
      setErr(
        inv && me
          ? `This invitation was sent to ${maskEmail(inv)} but you are logged in as ${maskEmail(me)}.`
          : "This invitation was sent to a different email address than the one currently logged in."
      );
      return;
    }

    if (status === "ok") {
      setMsg(
        kindQs === "project" ||
        inviteKind === "project"
          ? "Invitation accepted. Your project membership has been recorded. Screenplay collaborator writing remains disabled until authorship-safe permissions are activated."
          : "Invitation accepted. Welcome to the group."
      );
      return;
    }

    if (status === "declined") {
      setMsg("Invitation declined.");
      return;
    }

    if (status === "expired") {
      setErr("This invitation link is invalid or has expired.");
      return;
    }
  }, [status, kindQs, inviteKind, inviteEmailQs, meQs, inviteEmail, meEmail]);

  const inviteTargetEmail = useMemo(() => {
    const x = String(inviteEmailQs || inviteEmail || "").trim().toLowerCase();
    return x;
  }, [inviteEmailQs, inviteEmail]);

  const wrongAccount = useMemo(() => {
    if (!isAuthenticated) return false;
    if (!inviteTargetEmail) return false; // if we don't know invite email, don't accuse
    return meEmail && inviteTargetEmail && meEmail !== inviteTargetEmail;
  }, [isAuthenticated, meEmail, inviteTargetEmail]);

  if (booting) {
    return (
      <div className="page">
        <div className="card" style={{ maxWidth: 560, margin: "0 auto" }}>
          <h2 style={{ marginTop: 0 }}>Preparing invitation…</h2>
          <p style={{ marginTop: 8, color: "#475569" }}>Restoring your session.</p>
        </div>
      </div>
    );
  }

  // Missing token
  if (!token) {
    return (
      <div className="page">
        <div className="card" style={{ maxWidth: 560, margin: "0 auto" }}>
          <h2 style={{ marginTop: 0 }}>Invitation link is incomplete</h2>
          <div className="banner" style={{ marginTop: 10 }}>
            Missing invitation token. Please open the invitation link from your email again.
          </div>

          <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn" type="button" onClick={() => nav && nav("home")}>
              Go Home
            </button>
            <button className="btn" type="button" onClick={() => nav && nav("login")}>
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Not logged in
  if (!isAuthenticated) {
    return (
      <div className="page">
        <div className="card" style={{ maxWidth: 560, margin: "0 auto" }}>
          <h2 style={{ marginTop: 0 }}>Accept Group Invitation</h2>

          <p style={{ marginTop: 6, color: "#475569" }}>
            This invitation is protected. Please log in to accept it.
          </p>

          {intentQs === "decline" ? (
            <div
              className="banner"
              style={{
                marginTop: 10,
                background: "#fff7ed",
              }}
            >
              Nothing has been declined yet. Choose
              <b> Decline Invitation </b>
              below only if you do not want to join this group or project.
            </div>
          ) : null}

          {inviteTargetEmail ? (
            <div className="banner" style={{ marginTop: 10, background: "#fff7ed" }}>
              This invite was sent to <b>{maskEmail(inviteTargetEmail)}</b>.
              <div style={{ marginTop: 6, color: "#475569" }}>
                Log in using that same email address to avoid the “different email” error.
              </div>
            </div>
          ) : null}

          <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn" type="button" onClick={goLoginWithToken}>
              Login
            </button>

            <button className="btn" type="button" onClick={goRegisterWithToken}>
              Create Account
            </button>

            {(currentInviteKind() === "project" || currentInviteKind() === "story") ? (
              <button
                className="linkBtn"
                type="button"
                onClick={handleDeclineInvitation}
                disabled={busy}
                style={{
                  fontWeight: 800,
                }}
              >
                Decline Invitation
              </button>
            ) : null}

            <button className="linkBtn" type="button" onClick={() => nav && nav("home")} style={{ fontWeight: 800 }}>
              Go Home
            </button>
          </div>

          <div style={{ marginTop: 14, fontSize: 12, color: "#64748b" }}>
            Tip: If the link opens in a strange in-app browser, copy it into Chrome/Safari directly.
          </div>
        </div>
      </div>
    );
  }

  // Logged in
  return (
    <div className="page">
      <div className="card" style={{ maxWidth: 640, margin: "0 auto" }}>
        <h2 style={{ marginTop: 0 }}>Accept Group Invitation</h2>

        <p style={{ marginTop: 6 }}>
          Logged in as <b>{displayIdentity}</b>
        </p>

        {inviteTargetEmail ? (
          <p style={{ marginTop: 6, color: "#475569" }}>
            Invitation target: <b>{maskEmail(inviteTargetEmail)}</b>
          </p>
        ) : (
          <p style={{ marginTop: 6, color: "#475569" }}>
            Invitation target email is not visible here. If acceptance fails, it usually means you’re logged into the wrong account.
          </p>
        )}

        {busy ? (
          <div className="banner" style={{ marginTop: 10 }}>
            Accepting invitation…
          </div>
        ) : null}

        {err ? (
          <div className="banner" style={{ marginTop: 10 }}>
            {err}
          </div>
        ) : null}

        {msg ? (
          <div className="banner" style={{ marginTop: 10, background: "#dcfce7" }}>
            {msg}
          </div>
        ) : null}

        {wrongAccount ? (
          <div className="banner" style={{ marginTop: 12, background: "#fff7ed" }}>
            You are logged in with the wrong email for this invite.
            <div style={{ marginTop: 6, color: "#475569" }}>
              Log out, then log in using <b>{maskEmail(inviteTargetEmail)}</b>.
            </div>

            <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                className="btn"
                type="button"
                onClick={async () => {
                  try {
                    await logout();
                  } finally {
                    goLoginWithToken();
                  }
                }}
              >
                Log out and switch account
              </button>

              <button className="linkBtn" type="button" onClick={() => nav && nav("home")} style={{ fontWeight: 800 }}>
                Go Home
              </button>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn" type="button" onClick={handleProceedAccept} disabled={busy}>
              Accept Invitation
            </button>

            {(currentInviteKind() === "project" || currentInviteKind() === "story") ? (
              <button
                className="linkBtn"
                type="button"
                onClick={handleDeclineInvitation}
                disabled={busy}
                style={{
                  fontWeight: 800,
                }}
              >
                Decline Invitation
              </button>
            ) : null}

            {inviteKind === "project" ? (
              <button
                className="btn"
                type="button"
                onClick={() => {
                  window.location.assign(
                    "/my-projects/screenplay"
                  );
                }}
                disabled={busy}
              >
                Go to Project Hub
              </button>
            ) : (
              <button className="btn" type="button" onClick={() => nav && nav("storyHub")} disabled={busy}>
                Go to Story Hub
              </button>
            )}

            <button className="linkBtn" type="button" onClick={() => nav && nav("home")} disabled={busy} style={{ fontWeight: 800 }}>
              Go Home
            </button>
          </div>
        )}

        <div style={{ marginTop: 14, fontSize: 12, color: "#64748b" }}>
          Note: For the cleanest experience, the backend should redirect back to this page after acceptance.
        </div>
      </div>
    </div>
  );
}
